import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import KatilimGrafigi from "@/components/KatilimGrafigi";
import { Input } from "@/components/ui/input";
import {
  ArrowLeft,
  Search,
  X,
  Check,
  Ban,
  Phone,
  MessageCircle,
  RefreshCw,
  WifiOff,
  Send,
  Users,
  Undo2,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { normalize } from "@/lib/utils";
import { STATUSES, STATUS_META, OUR_SIDE, ENTITY_META, DEFAULT_ELECTION_AT } from "@/lib/constants";
import { telefonlar, numaraYaz, telLinki, waLinki } from "@/lib/telefon";

// Oy işaretleri 15 saniyede bir, üye listesi 3 dakikada bir tazelenir.
// Farklı yerlerdeki ekip aynı tabloyu görür; ağır istek seyrek kalır.
const OY_ARALIK = 15 * 1000;
const UYE_ARALIK = 3 * 60 * 1000;
const SAYFA = 60;

const SEKMELER = [
  { key: "tumu", label: "Tümü" },
  { key: "bekleniyor", label: "Bekleniyor" },
  { key: "geldi", label: "Geldi" },
  { key: "gelmedi", label: "Gelmedi" },
];

const saatYaz = (iso) => {
  try {
    return new Date(iso).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

const yuzde = (a, b) => (b > 0 ? Math.round((a / b) * 100) : 0);

function oyDurumu(oy) {
  return oy?.vote === "geldi" || oy?.vote === "gelmedi" ? oy.vote : "bekleniyor";
}

function Cubuk({ oran, renk }) {
  return (
    <div className="h-2 w-full rounded-full bg-neutral-100 dark:bg-neutral-800 overflow-hidden">
      <div
        className="h-full rounded-full transition-[width] duration-500"
        style={{ width: `${Math.min(100, oran)}%`, background: renk }}
      />
    </div>
  );
}

const Satir = memo(function Satir({ uye, oy, canWrite, yaziliyor, acik, onIsaretle, onTelAc }) {
  const meta = STATUS_META[uye.status] || STATUS_META.Beyaz;
  const em = ENTITY_META[uye.entity_type] || null;
  const durum = oyDurumu(oy);
  const numaralar = acik ? telefonlar(uye.phone) : null;
  const telVar = Boolean((uye.phone || "").replace(/\D/g, ""));

  // Düğmeler basınca ne olacağını açıkça söyler (sistemi yaşça büyük üyeler de
  // kullanıyor): işaretli üyede "işaretini kaldır" ve "... olarak değiştir".
  // Seçili duruma tekrar basmak işareti kaldırır (bkz. isaretle).
  const YESIL = "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 enabled:hover:border-emerald-500 enabled:hover:text-emerald-700 dark:enabled:hover:text-emerald-400";
  const KIRMIZI = "bg-white dark:bg-neutral-900 border-neutral-200 dark:border-neutral-700 text-neutral-700 dark:text-neutral-200 enabled:hover:border-red-500 enabled:hover:text-red-700 dark:enabled:hover:text-red-400";
  const KALDIR = "bg-neutral-100 dark:bg-neutral-800 border-neutral-300 dark:border-neutral-600 text-neutral-800 dark:text-neutral-100 enabled:hover:bg-neutral-200 dark:enabled:hover:bg-neutral-700";
  const dugmeler =
    durum === "geldi"
      ? [
          { secim: "geldi", testid: "geldi", label: "Geldi işaretini kaldır", Ikon: Undo2, stil: KALDIR },
          { secim: "gelmedi", testid: "gelmedi", label: "Gelmedi olarak değiştir", Ikon: Ban, stil: "bg-white dark:bg-neutral-900 border-red-200 dark:border-red-900 text-red-700 dark:text-red-400 enabled:hover:bg-red-50 dark:enabled:hover:bg-red-950/40" },
        ]
      : durum === "gelmedi"
      ? [
          { secim: "gelmedi", testid: "gelmedi", label: "Gelmedi işaretini kaldır", Ikon: Undo2, stil: KALDIR },
          { secim: "geldi", testid: "geldi", label: "Geldi olarak değiştir", Ikon: Check, stil: "bg-white dark:bg-neutral-900 border-emerald-200 dark:border-emerald-900 text-emerald-700 dark:text-emerald-400 enabled:hover:bg-emerald-50 dark:enabled:hover:bg-emerald-950/40" },
        ]
      : [
          { secim: "geldi", testid: "geldi", label: "Geldi", Ikon: Check, stil: YESIL },
          { secim: "gelmedi", testid: "gelmedi", label: "Gelmedi", Ikon: Ban, stil: KIRMIZI },
        ];

  const kart =
    durum === "geldi"
      ? "bg-emerald-50/80 dark:bg-emerald-950/30"
      : durum === "gelmedi"
      ? "bg-red-50/60 dark:bg-red-950/20"
      : "bg-white dark:bg-neutral-900";

  return (
    <li
      data-testid={`sandik-satir-${uye.id}`}
      className={`rounded-xl border border-neutral-200 dark:border-neutral-800 border-l-4 ${kart} p-3 sm:p-3.5`}
      // Sol şerit durum rengini satır içi stil ile alır: ikinci bir border-*
      // sınıfı eklemek Tailwind sırasına göre renklerden birini eziyordu.
      style={{ borderLeftColor: uye.status === "Beyaz" ? "#D4D4D4" : meta.dot }}
    >
      <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="font-semibold text-[14px] sm:text-[15px] leading-snug text-neutral-950 dark:text-neutral-50">
                {uye.order_no != null && (
                  <span className="font-mono text-[12px] text-neutral-400 dark:text-neutral-500 mr-1.5">
                    #{uye.order_no}
                  </span>
                )}
                {uye.name}
              </div>
              {uye.company && (
                <div className="text-[12px] text-neutral-500 dark:text-neutral-400 truncate mt-0.5">
                  {uye.company}
                </div>
              )}
            </div>
            {telVar && (
              <button
                type="button"
                onClick={() => onTelAc(uye.id)}
                aria-label="Telefonlar"
                aria-expanded={acik}
                className={`sm:hidden shrink-0 h-9 w-9 rounded-lg border flex items-center justify-center ${
                  acik
                    ? "bg-neutral-950 text-white border-neutral-950 dark:bg-white dark:text-neutral-950"
                    : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300"
                }`}
              >
                <Phone className="h-4 w-4" />
              </button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className={`inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-md ${meta.bg} ${meta.text}`}>
              <span className="h-1.5 w-1.5 rounded-full" style={{ background: meta.dot }} />
              {meta.short}
            </span>
            {em && (
              <span className={`text-[10.5px] font-medium px-1.5 py-0.5 rounded-md ${em.bg} ${em.text}`}>
                {em.short} · {uye.entity_type === "Şahıs" ? "kimlikle" : "yetki belgesiyle"}
              </span>
            )}
            {durum !== "bekleniyor" && (
              <span
                className={`inline-flex items-center gap-1 text-[11px] font-semibold px-1.5 py-0.5 rounded-md ${
                  durum === "geldi"
                    ? "bg-emerald-600 text-white"
                    : "bg-red-600 text-white"
                }`}
              >
                {durum === "geldi" ? "✓ Oy kullandı" : "✗ Gelmedi"}
                {oy?.vote_at ? ` · ${saatYaz(oy.vote_at)}` : ""}
              </span>
            )}
          </div>
        </div>

        <div
          className={`grid gap-2 sm:flex sm:items-center sm:shrink-0 ${
            durum === "bekleniyor" ? "grid-cols-2" : "grid-cols-1"
          }`}
        >
          {telVar && (
            <button
              type="button"
              onClick={() => onTelAc(uye.id)}
              aria-label="Telefonlar"
              aria-expanded={acik}
              className={`hidden sm:flex shrink-0 h-10 w-10 rounded-lg border items-center justify-center ${
                acik
                  ? "bg-neutral-950 text-white border-neutral-950 dark:bg-white dark:text-neutral-950"
                  : "border-neutral-200 dark:border-neutral-700 text-neutral-600 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              }`}
            >
              <Phone className="h-4 w-4" />
            </button>
          )}
          {dugmeler.map((b) => (
            <button
              key={b.secim}
              type="button"
              disabled={!canWrite || yaziliyor}
              onClick={() => onIsaretle(uye, b.secim)}
              data-testid={`sandik-${b.testid}-${uye.id}`}
              className={`h-11 sm:h-10 px-3 rounded-lg border text-[13px] sm:text-sm font-semibold inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-60 ${
                durum === "bekleniyor" ? "sm:w-28" : "sm:w-auto"
              } ${b.stil}`}
            >
              <b.Ikon className="h-4 w-4 shrink-0" />
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {acik && numaralar && (
        <div className="mt-3 pt-3 border-t border-neutral-200/70 dark:border-neutral-800 space-y-2">
          {numaralar.length === 0 && (
            <div className="text-xs text-neutral-500">Aranabilir numara bulunamadı.</div>
          )}
          {numaralar.map((t) => (
            <div key={t.numara} className="flex items-center gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-mono text-[13px] text-neutral-900 dark:text-neutral-100">
                  {numaraYaz(t.numara)}
                </div>
                {t.etiket && (
                  <div className="text-[11px] text-neutral-500 dark:text-neutral-400 truncate">{t.etiket}</div>
                )}
              </div>
              <a
                href={telLinki(t.numara)}
                className="h-9 px-3 rounded-lg border border-neutral-200 dark:border-neutral-700 text-[13px] font-medium inline-flex items-center gap-1.5 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-800"
              >
                <Phone className="h-3.5 w-3.5" />
                Ara
              </a>
              {t.cep && (
                <a
                  href={waLinki(t.numara)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="h-9 px-3 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 text-[13px] font-medium inline-flex items-center gap-1.5 text-emerald-800 dark:text-emerald-300 hover:bg-emerald-100"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                  WhatsApp
                </a>
              )}
            </div>
          ))}
        </div>
      )}
    </li>
  );
});

export default function SandikPage() {
  const { canWrite } = useAuth();
  const [members, setMembers] = useState([]);
  const [oylar, setOylar] = useState(() => new Map());
  const [loading, setLoading] = useState(true);
  const [sonSenkron, setSonSenkron] = useState(null);
  const [baglantiYok, setBaglantiYok] = useState(false);
  const [simdi, setSimdi] = useState(Date.now());
  const [secimAt, setSecimAt] = useState(DEFAULT_ELECTION_AT);

  const [arama, setArama] = useState("");
  const [sekme, setSekme] = useState("tumu");
  const [grup, setGrup] = useState("tumu");
  const [limit, setLimit] = useState(SAYFA);
  const [acikTel, setAcikTel] = useState(null);
  const [yazilanlar, setYazilanlar] = useState(() => new Set());

  // id → son yerel yazım zamanı. Yazım sürerken Infinity: o sırada başlamış bir
  // yoklama sunucudan eski hâli getirirse ekrandaki yeni işaret geri dönmesin.
  const yazimRef = useRef(new Map());
  const kontrolRef = useRef(null);
  const [kontrolTop, setKontrolTop] = useState(56);

  const oylariUygula = useCallback((liste, t0) => {
    setOylar((onceki) => {
      const sonraki = new Map();
      for (const o of liste) sonraki.set(o.id, o);
      for (const [id, zaman] of yazimRef.current) {
        if (zaman >= t0) {
          if (onceki.has(id)) sonraki.set(id, onceki.get(id));
          else sonraki.delete(id);
        }
      }
      return sonraki;
    });
  }, []);

  const oylariYokla = useCallback(async () => {
    const t0 = Date.now();
    try {
      const { votes } = await api.votes();
      oylariUygula(votes || [], t0);
      setSonSenkron(Date.now());
      setBaglantiYok(false);
    } catch {
      setBaglantiYok(true);
    }
  }, [oylariUygula]);

  const uyeleriYukle = useCallback(async () => {
    const t0 = Date.now();
    try {
      const ms = await api.listMembers();
      setMembers(ms);
      oylariUygula(
        ms.filter((m) => m.vote === "geldi" || m.vote === "gelmedi").map((m) => ({
          id: m.id,
          vote: m.vote,
          vote_at: m.vote_at,
          vote_by: m.vote_by,
        })),
        t0
      );
      setSonSenkron(Date.now());
      setBaglantiYok(false);
    } catch {
      setBaglantiYok(true);
      toast.error("Üyeler yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, [oylariUygula]);

  // Seçim tarihi panelden değiştirilebildiği için sunucudan okunur;
  // gelmezse varsayılan tarih kullanılır.
  useEffect(() => {
    api
      .settings()
      .then((s) => s?.election_at && setSecimAt(s.election_at))
      .catch(() => {});
  }, []);

  useEffect(() => {
    uyeleriYukle();
    const oyZamanlayici = setInterval(() => {
      if (document.visibilityState === "visible") oylariYokla();
    }, OY_ARALIK);
    const uyeZamanlayici = setInterval(() => {
      if (document.visibilityState === "visible") uyeleriYukle();
    }, UYE_ARALIK);
    const saat = setInterval(() => setSimdi(Date.now()), 5000);
    // Ana ekrana eklenmiş uygulama arka plandan dönünce hemen tazelensin.
    const gorunurluk = () => {
      if (document.visibilityState === "visible") uyeleriYukle();
    };
    document.addEventListener("visibilitychange", gorunurluk);
    return () => {
      clearInterval(oyZamanlayici);
      clearInterval(uyeZamanlayici);
      clearInterval(saat);
      document.removeEventListener("visibilitychange", gorunurluk);
    };
  }, [uyeleriYukle, oylariYokla]);

  // Arama ve süzgeç çubuğu başlığın hemen altına yapışsın; başlık yüksekliği
  // ekrana göre değiştiği için ölçülüyor.
  useEffect(() => {
    const olc = () => {
      const h = document.querySelector("header");
      if (h) setKontrolTop(h.getBoundingClientRect().height);
    };
    olc();
    window.addEventListener("resize", olc);
    return () => window.removeEventListener("resize", olc);
  }, []);

  useEffect(() => setLimit(SAYFA), [arama, sekme, grup]);

  const tekOy = (id, oy) =>
    setOylar((onceki) => {
      const sonraki = new Map(onceki);
      if (oy && oy.vote) sonraki.set(id, oy);
      else sonraki.delete(id);
      return sonraki;
    });

  const oyYaz = async (uye, hedef, onceki) => {
    yazimRef.current.set(uye.id, Infinity);
    setYazilanlar((s) => new Set(s).add(uye.id));
    tekOy(uye.id, hedef ? { id: uye.id, vote: hedef, vote_at: new Date().toISOString() } : null);
    try {
      const sonuc = await api.setVote(uye.id, hedef);
      tekOy(uye.id, sonuc.vote ? sonuc : null);
      return true;
    } catch {
      tekOy(uye.id, onceki);
      toast.error("İşaret kaydedilemedi — bağlantıyı kontrol edip tekrar deneyin");
      return false;
    } finally {
      yazimRef.current.set(uye.id, Date.now());
      setYazilanlar((s) => {
        const n = new Set(s);
        n.delete(uye.id);
        return n;
      });
    }
  };

  // Satırlar her yoklamada yeniden çizilmesin diye işaretleme fonksiyonu
  // güncel işaretleri ref'ten okur.
  const oylarRef = useRef(oylar);
  oylarRef.current = oylar;

  const isaretle = useCallback(
    async (uye, secim) => {
      if (!canWrite) return;
      const onceki = oylarRef.current.get(uye.id) || null;
      // Seçili düğmeye tekrar basmak işareti kaldırır (Bekleniyor'a döner).
      const hedef = onceki?.vote === secim ? null : secim;
      const ok = await oyYaz(uye, hedef, onceki);
      if (!ok) return;
      const mesaj = hedef === "geldi" ? "Geldi" : hedef === "gelmedi" ? "Gelmedi" : "İşaret kaldırıldı";
      toast.success(`${uye.name} — ${mesaj}`, {
        duration: 4000,
        action: {
          label: "Geri al",
          onClick: () => oyYaz(uye, onceki?.vote ?? null, oylarRef.current.get(uye.id) || null),
        },
      });
    },
    // oyYaz yalnız kararlı state ayarlayıcılarını ve ref'leri kullanıyor
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canWrite]
  );

  const telAc = useCallback((id) => setAcikTel((a) => (a === id ? null : id)), []);

  // ---- Özet ----
  const ozet = useMemo(() => {
    const s = { toplam: members.length, geldi: 0, gelmedi: 0, bekleniyor: 0, bizim: 0, bizimGeldi: 0 };
    const durumlar = Object.fromEntries(STATUSES.map((d) => [d, { toplam: 0, geldi: 0 }]));
    for (const m of members) {
      const d = oyDurumu(oylar.get(m.id));
      s[d] += 1;
      if (OUR_SIDE.includes(m.status)) {
        s.bizim += 1;
        if (d === "geldi") s.bizimGeldi += 1;
      }
      if (durumlar[m.status]) {
        durumlar[m.status].toplam += 1;
        if (d === "geldi") durumlar[m.status].geldi += 1;
      }
    }
    return { ...s, durumlar };
  }, [members, oylar]);

  // ---- Liste ----
  const aramaVar = arama.trim().length > 0;
  const grupListesi = useMemo(() => {
    if (grup === "bizim") return members.filter((m) => OUR_SIDE.includes(m.status));
    if (grup !== "tumu") return members.filter((m) => m.status === grup);
    return members;
  }, [members, grup]);

  const sekmeSayilari = useMemo(() => {
    const c = { tumu: grupListesi.length, bekleniyor: 0, geldi: 0, gelmedi: 0 };
    for (const m of grupListesi) c[oyDurumu(oylar.get(m.id))] += 1;
    return c;
  }, [grupListesi, oylar]);

  const liste = useMemo(() => {
    let l;
    if (aramaVar) {
      // Arama her zaman bütün üyelerde yapılır: sandıkta gelen kişi hangi
      // grupta olursa olsun bulunabilsin.
      const raw = arama.trim();
      const sira = raw.match(/^#?\s*(\d{1,3})$/);
      const tam = sira ? members.filter((m) => m.order_no === Number(sira[1])) : [];
      if (tam.length > 0) return tam;
      const q = normalize(raw);
      const terimler = q.split(" ").filter(Boolean);
      const rakam = raw.replace(/\D/g, "");
      l = members.filter((m) => {
        const hay = normalize([m.name, m.company, m.member_no, m.trade_reg_no].filter(Boolean).join(" "));
        if (terimler.length && terimler.every((t) => hay.includes(t))) return true;
        if (rakam.length >= 3) {
          if ((m.phone || "").replace(/\D/g, "").includes(rakam)) return true;
          if ((m.member_no || "").includes(rakam) || (m.trade_reg_no || "").includes(rakam)) return true;
        }
        return false;
      });
    } else {
      l = sekme === "tumu" ? grupListesi : grupListesi.filter((m) => oyDurumu(oylar.get(m.id)) === sekme);
    }
    const siraSirasi = (a, b) =>
      (a.order_no ?? 1e9) - (b.order_no ?? 1e9) || String(a.name).localeCompare(String(b.name), "tr");
    if (!aramaVar && (sekme === "geldi" || sekme === "gelmedi")) {
      // İşaretlenenler en yeniden eskiye: son gelen en üstte.
      return [...l].sort((a, b) =>
        String(oylar.get(b.id)?.vote_at || "").localeCompare(String(oylar.get(a.id)?.vote_at || ""))
      );
    }
    return [...l].sort(siraSirasi);
  }, [members, grupListesi, oylar, arama, aramaVar, sekme]);

  const gorunen = liste.slice(0, limit);
  const saniyeOnce = sonSenkron ? Math.max(0, Math.round((simdi - sonSenkron) / 1000)) : null;

  const gruplar = [
    { key: "tumu", label: "Tüm üyeler", sayi: members.length },
    { key: "bizim", label: "Bizim taraf", sayi: ozet.bizim },
    ...STATUSES.map((s) => ({ key: s, label: STATUS_META[s].short, sayi: ozet.durumlar[s]?.toplam ?? 0, dot: STATUS_META[s].dot })),
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] dark:bg-neutral-950">
      <Header />
      <main className="max-w-6xl mx-auto px-4 py-5 sm:py-6 space-y-4">
        {/* Başlık */}
        <div className="flex items-start gap-3">
          <Link
            to="/"
            aria-label="Panele dön"
            className="mt-1 h-9 w-9 shrink-0 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex items-center justify-center text-neutral-600 dark:text-neutral-300 hover:text-neutral-950 dark:hover:text-white"
          >
            <ArrowLeft className="h-4 w-4" />
          </Link>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
              Seçim günü
            </div>
            <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mt-0.5 text-neutral-950 dark:text-neutral-50">
              Sandık Takibi
            </h1>
            <p className="text-[12.5px] text-neutral-500 dark:text-neutral-400 mt-1">
              Oy kullanmaya geleni <b className="text-emerald-700 dark:text-emerald-400">Geldi</b>, gelmeyeceği
              kesinleşeni <b className="text-red-700 dark:text-red-400">Gelmedi</b> olarak işaretleyin. Yanlış
              işaret "işaretini kaldır" düğmesiyle geri alınır.
            </p>
          </div>
          <Link
            to="/davet"
            className="hidden sm:inline-flex shrink-0 h-10 px-3.5 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-sm font-medium items-center gap-1.5 hover:bg-emerald-100 dark:hover:bg-emerald-950/70"
            data-testid="davet-link"
          >
            <Send className="h-4 w-4" />
            WhatsApp Davet
          </Link>
        </div>

        {/* Özet */}
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-4">
          <div className="lg:col-span-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-2xl p-4 sm:p-5">
            <div className="grid grid-cols-2 gap-4 sm:gap-6">
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
                  Oy kullanan
                </div>
                <div className="font-heading tabular-nums mt-1 text-neutral-950 dark:text-neutral-50">
                  <span className="text-3xl sm:text-4xl font-semibold">{ozet.geldi}</span>
                  <span className="text-base text-neutral-400"> / {ozet.toplam}</span>
                </div>
                <div className="mt-2">
                  <Cubuk oran={yuzde(ozet.geldi, ozet.toplam)} renk="#10B981" />
                </div>
                <div className="text-[11px] text-neutral-500 mt-1 tabular-nums">%{yuzde(ozet.geldi, ozet.toplam)} katılım</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.15em] font-semibold text-neutral-500 dark:text-neutral-400">
                  Bizim taraf
                </div>
                <div className="font-heading tabular-nums mt-1 text-neutral-950 dark:text-neutral-50">
                  <span className="text-3xl sm:text-4xl font-semibold">{ozet.bizimGeldi}</span>
                  <span className="text-base text-neutral-400"> / {ozet.bizim}</span>
                </div>
                <div className="mt-2">
                  <Cubuk oran={yuzde(ozet.bizimGeldi, ozet.bizim)} renk="#047857" />
                </div>
                <div className="text-[11px] text-neutral-500 mt-1 tabular-nums">
                  {ozet.bizim - ozet.bizimGeldi} kişi henüz gelmedi
                </div>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-2 mt-4">
              {[
                { k: "geldi", l: "Geldi", c: "text-emerald-700 dark:text-emerald-400", b: "bg-emerald-50 dark:bg-emerald-950/40" },
                { k: "bekleniyor", l: "Bekleniyor", c: "text-neutral-700 dark:text-neutral-200", b: "bg-neutral-100 dark:bg-neutral-800/70" },
                { k: "gelmedi", l: "Gelmedi", c: "text-red-700 dark:text-red-400", b: "bg-red-50 dark:bg-red-950/30" },
              ].map((x) => (
                <button
                  key={x.k}
                  type="button"
                  onClick={() => {
                    setArama("");
                    setSekme(x.k);
                  }}
                  className={`rounded-xl ${x.b} px-3 py-2 text-left`}
                >
                  <div className={`font-heading text-xl font-semibold tabular-nums ${x.c}`}>{ozet[x.k]}</div>
                  <div className="text-[9.5px] sm:text-[10.5px] uppercase tracking-[0.04em] sm:tracking-[0.1em] font-semibold text-neutral-500 dark:text-neutral-400">
                    {x.l}
                  </div>
                </button>
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2.5 mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800">
              {STATUSES.map((s) => {
                const d = ozet.durumlar[s] || { toplam: 0, geldi: 0 };
                const meta = STATUS_META[s];
                return (
                  <div key={s} className="min-w-0">
                    <div className="flex items-center justify-between gap-2 text-[11.5px]">
                      <span className="flex items-center gap-1.5 min-w-0 text-neutral-600 dark:text-neutral-300">
                        <span className="h-2 w-2 rounded-full shrink-0 ring-1 ring-black/5" style={{ background: meta.dot }} />
                        <span className="truncate">{meta.short}</span>
                      </span>
                      <span className="tabular-nums font-semibold text-neutral-900 dark:text-neutral-100">
                        {d.geldi}
                        <span className="text-neutral-400 font-normal">/{d.toplam}</span>
                      </span>
                    </div>
                    <div className="mt-1">
                      <Cubuk oran={yuzde(d.geldi, d.toplam)} renk={s === "Beyaz" ? "#A3A3A3" : meta.dot} />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <KatilimGrafigi oylar={oylar} secimAt={secimAt} />
        </section>

        {/* Davet (mobil) + eşitleme durumu */}
        <div className="flex flex-col sm:flex-row sm:items-center gap-2">
          <Link
            to="/davet"
            className="sm:hidden h-11 rounded-xl border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300 text-sm font-medium flex items-center justify-center gap-1.5"
          >
            <Send className="h-4 w-4" />
            WhatsApp ile toplu davet gönder
          </Link>
          <div className="sm:ml-auto flex items-center gap-2 text-[11.5px] text-neutral-500 dark:text-neutral-400">
            {baglantiYok ? (
              <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-400 font-medium">
                <WifiOff className="h-3.5 w-3.5" />
                Bağlantı yok{saniyeOnce != null ? ` — son güncelleme ${Math.round(saniyeOnce / 60)} dk önce` : ""}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {saniyeOnce == null ? "Yükleniyor…" : saniyeOnce < 5 ? "Az önce güncellendi" : `${saniyeOnce} sn önce güncellendi`}
              </span>
            )}
            <button
              type="button"
              onClick={uyeleriYukle}
              className="h-8 w-8 rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex items-center justify-center hover:text-neutral-900 dark:hover:text-white"
              aria-label="Şimdi yenile"
            >
              <RefreshCw className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Arama + süzgeçler: başlığın altına yapışık */}
        <div
          ref={kontrolRef}
          className="sticky z-30 -mx-4 px-4 pt-2 pb-3 bg-[#F8F9FA]/95 dark:bg-neutral-950/95 backdrop-blur-md space-y-2.5"
          style={{ top: kontrolTop }}
        >
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
            <Input
              value={arama}
              onChange={(e) => setArama(e.target.value)}
              placeholder="İsim, firma, sıra no ya da telefon…"
              className="h-12 pl-10 pr-10 text-[15px] bg-white dark:bg-neutral-900 rounded-xl"
              data-testid="sandik-arama"
            />
            {aramaVar && (
              <button
                type="button"
                onClick={() => setArama("")}
                aria-label="Aramayı temizle"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-lg flex items-center justify-center text-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-800"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          {aramaVar ? (
            <div className="text-[12px] text-neutral-500 dark:text-neutral-400">
              Arama tüm üyelerde yapılıyor · {liste.length} sonuç
            </div>
          ) : (
            <>
              <div className="grid grid-cols-4 gap-1 p-1 rounded-xl bg-neutral-200/60 dark:bg-neutral-800/70">
                {SEKMELER.map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => setSekme(s.key)}
                    aria-pressed={sekme === s.key}
                    className={`relative h-11 sm:h-9 rounded-lg text-[12.5px] sm:text-sm font-medium leading-tight transition-colors ${
                      sekme === s.key ? "text-neutral-950 dark:text-white" : "text-neutral-600 dark:text-neutral-400"
                    }`}
                  >
                    {/* Seçili sekmenin beyaz zemini bir sekmeden ötekine kayar */}
                    {sekme === s.key && (
                      <motion.span
                        layoutId="sandik-sekme-zemin"
                        className="absolute inset-0 rounded-lg bg-white dark:bg-neutral-950 shadow-sm"
                        transition={{ type: "spring", stiffness: 420, damping: 36 }}
                      />
                    )}
                    <span className="relative flex flex-col sm:flex-row items-center justify-center sm:gap-1">
                      {s.label}
                      <span className="tabular-nums text-[10.5px] sm:text-[11px] text-neutral-400">{sekmeSayilari[s.key]}</span>
                    </span>
                  </button>
                ))}
              </div>
              <div className="flex gap-1.5 overflow-x-auto -mx-4 px-4 pb-0.5 [scrollbar-width:none]">
                {gruplar.map((g) => (
                  <button
                    key={g.key}
                    type="button"
                    onClick={() => setGrup(g.key)}
                    aria-pressed={grup === g.key}
                    className={`shrink-0 h-8 px-3 rounded-full border text-[12px] font-medium inline-flex items-center gap-1.5 transition-colors ${
                      grup === g.key
                        ? "bg-neutral-950 text-white border-neutral-950 dark:bg-white dark:text-neutral-950 dark:border-white"
                        : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-700"
                    }`}
                  >
                    {g.key === "bizim" && <Users className="h-3.5 w-3.5" />}
                    {g.dot && <span className="h-2 w-2 rounded-full ring-1 ring-black/10" style={{ background: g.dot }} />}
                    {g.label}
                    <span className={`tabular-nums ${grup === g.key ? "opacity-70" : "text-neutral-400"}`}>{g.sayi}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Liste */}
        {loading ? (
          <div className="py-16 text-center text-sm text-neutral-500">Üyeler yükleniyor…</div>
        ) : liste.length === 0 ? (
          <div className="py-14 text-center text-sm text-neutral-500 dark:text-neutral-400 border border-dashed border-neutral-300 dark:border-neutral-700 rounded-2xl">
            {aramaVar ? "Aramayla eşleşen üye yok." : "Bu süzgeçte üye yok."}
          </div>
        ) : (
          <>
            <ul className="space-y-2" data-testid="sandik-liste">
              {gorunen.map((m) => (
                <Satir
                  key={m.id}
                  uye={m}
                  oy={oylar.get(m.id)}
                  canWrite={canWrite}
                  yaziliyor={yazilanlar.has(m.id)}
                  acik={acikTel === m.id}
                  onIsaretle={isaretle}
                  onTelAc={telAc}
                />
              ))}
            </ul>
            {liste.length > gorunen.length && (
              <button
                type="button"
                onClick={() => setLimit((l) => l + SAYFA)}
                className="w-full h-11 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 text-sm font-medium text-neutral-700 dark:text-neutral-200"
              >
                Daha fazla göster ({liste.length - gorunen.length} kişi daha)
              </button>
            )}
          </>
        )}
        {!canWrite && (
          <div className="text-center text-[12px] text-neutral-500 pb-4">
            Görüntüleyici olarak girdiniz; işaretleme yapamazsınız.
          </div>
        )}
      </main>
    </div>
  );
}
